import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { Location } from "@/types/weather";
import type { Webcam, WebcamsResponse } from "@shared/webcams";
import SnapshotViewer from "./SnapshotViewer";

interface LiveImagesProps {
  location: Location;
}

type ImageErrors = Record<string, true>;

function imageKey(camera: Webcam): string {
  return JSON.stringify([camera.id, camera.previewUrl]);
}

function dayHistoryUrl(camera: Webcam): string | null {
  const canonicalUrl = `https://webcams.windy.com/webcams/public/embed/player/${camera.id}/day`;
  return camera.playerUrl === canonicalUrl ? canonicalUrl : null;
}

function timestamp(value: string): number | null {
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function sortNewestFirst(cameras: Webcam[]): Webcam[] {
  return cameras
    .map((camera, index) => ({ camera, index, updatedAt: timestamp(camera.lastUpdatedOn) }))
    .sort((a, b) => {
      if (a.updatedAt === null && b.updatedAt !== null) return 1;
      if (a.updatedAt !== null && b.updatedAt === null) return -1;
      if (a.updatedAt !== null && b.updatedAt !== null && a.updatedAt !== b.updatedAt) {
        return b.updatedAt - a.updatedAt;
      }
      return a.index - b.index;
    })
    .map(({ camera }) => camera);
}

function relativeAge(value: string): { label: string; ageMs: number | null } {
  const updatedAt = timestamp(value);
  if (updatedAt === null) return { label: "Update time unavailable", ageMs: null };
  const ageMs = Math.max(0, Date.now() - updatedAt);
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 1) return { label: "Updated just now", ageMs };
  if (minutes < 60) return { label: `Updated ${minutes} min ago`, ageMs };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { label: `Updated ${hours} hr${hours === 1 ? "" : "s"} ago`, ageMs };
  const days = Math.floor(hours / 24);
  return { label: `Updated ${days} day${days === 1 ? "" : "s"} ago`, ageMs };
}

function LoadingSnapshots() {
  return (
    <div aria-label="Loading nearby cameras" className="space-y-4">
      {[0, 1].map((item) => (
        <div key={item} className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#10211e]">
          <div className="aspect-[16/9] animate-pulse bg-[#172b27]" />
          <div className="space-y-3 p-5">
            <div className="h-4 w-2/5 animate-pulse rounded bg-emerald-50/[0.08]" />
            <div className="h-3 w-1/3 animate-pulse rounded bg-emerald-50/[0.05]" />
          </div>
        </div>
      ))}
    </div>
  );
}

function LiveImagesForLocation({ location }: LiveImagesProps) {
  const [failedImages, setFailedImages] = useState<ImageErrors>({});
  const [refreshingCameraIds, setRefreshingCameraIds] = useState<Set<number>>(() => new Set());
  const [imageRevisions, setImageRevisions] = useState<Record<string, number>>({});
  const [selectedCameraId, setSelectedCameraId] = useState<number | null>(null);
  const viewerTrigger = useRef<HTMLButtonElement | null>(null);

  const webcamsQuery = useQuery({
    queryKey: ["/api/locations", location.id, "webcams"],
    queryFn: async () => {
      const response = await apiRequest(`/api/locations/${location.id}/webcams`, { method: "GET" });
      return (await response.json()) as WebcamsResponse;
    },
    // Signed preview URLs may expire while cached; always obtain fresh metadata on mount.
    staleTime: 60_000,
    refetchOnMount: "always",
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const cameras = webcamsQuery.data?.webcams ?? [];
  const sortedCameras = sortNewestFirst(cameras);
  const selectedCamera = selectedCameraId === null
    ? undefined
    : cameras.find((camera) => camera.id === selectedCameraId);
  const cameraImagesSignature = JSON.stringify(cameras.map((camera) => [camera.id, camera.previewUrl]));

  useEffect(() => {
    const currentImages = new Set(
      (JSON.parse(cameraImagesSignature) as [number, string][]).map(([id, previewUrl]) => JSON.stringify([id, previewUrl])),
    );
    setFailedImages((previous) => {
      const next = Object.fromEntries(Object.entries(previous).filter(([key]) => currentImages.has(key))) as ImageErrors;
      return Object.keys(next).length === Object.keys(previous).length ? previous : next;
    });
    setImageRevisions((previous) => {
      const next = Object.fromEntries(Object.entries(previous).filter(([key]) => currentImages.has(key)));
      return Object.keys(next).length === Object.keys(previous).length ? previous : next;
    });
  }, [cameraImagesSignature]);

  useEffect(() => {
    if (selectedCameraId !== null && !selectedCamera) setSelectedCameraId(null);
  }, [selectedCamera, selectedCameraId]);

  const retryMetadata = () => {
    void webcamsQuery.refetch();
  };

  const refreshCameraImage = async (cameraId: number) => {
    if (refreshingCameraIds.has(cameraId)) return;
    setRefreshingCameraIds((previous) => new Set(previous).add(cameraId));
    try {
      const result = await webcamsQuery.refetch();
      if (result.isError || !result.data) return;

      const refreshedCamera = result.data.webcams.find((camera) => camera.id === cameraId);
      if (!refreshedCamera) return;
      const key = imageKey(refreshedCamera);
      setFailedImages((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
      setImageRevisions((previous) => ({ ...previous, [key]: (previous[key] ?? 0) + 1 }));
    } catch {
      // Keep the image failure visible when fresh metadata cannot be obtained.
    } finally {
      setRefreshingCameraIds((previous) => {
        const next = new Set(previous);
        next.delete(cameraId);
        return next;
      });
    }
  };

  const setImageFailed = (key: string) => {
    setFailedImages((previous) => previous[key] ? previous : { ...previous, [key]: true });
  };

  const retryButton = (camera: Webcam) => (
    <button
      type="button"
      onClick={() => void refreshCameraImage(camera.id)}
      disabled={refreshingCameraIds.has(camera.id)}
      className="rounded-lg border border-emerald-300/25 px-3 py-2 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-300/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-300 disabled:cursor-wait disabled:opacity-55"
      aria-label={`Refresh image for ${camera.title}`}
    >
      {refreshingCameraIds.has(camera.id) ? "Refreshing…" : "Refresh image"}
    </button>
  );

  return (
    <section className="ls-reading-width px-4 pb-12 pt-5 sm:pt-7" aria-label={`Live images near ${location.name}`}>
      <div className="mb-6 flex flex-col gap-4 border-b border-white/[0.08] pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-300/70">Local camera network</p>
        </div>
        <span className="w-fit rounded-full border border-emerald-300/15 bg-emerald-300/[0.06] px-3 py-1.5 text-[11px] font-medium text-emerald-100/70">
          Within 25 km
        </span>
      </div>

      {webcamsQuery.isPending && !webcamsQuery.data ? (
        <LoadingSnapshots />
      ) : webcamsQuery.isError && !webcamsQuery.data ? (
        <div className="rounded-2xl border border-rose-200/15 bg-rose-200/[0.04] px-6 py-10 text-center">
          <h2 className="text-base font-semibold text-[#f1eeea]">Camera images are unavailable right now</h2>
          <p className="mt-2 text-sm text-emerald-50/50">Try again in a moment.</p>
          <button
            type="button"
            onClick={retryMetadata}
            disabled={webcamsQuery.isFetching}
            className="mt-5 rounded-lg border border-emerald-300/25 px-4 py-2 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-300/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-300 disabled:cursor-wait disabled:opacity-55"
          >
            {webcamsQuery.isFetching ? "Retrying…" : "Retry"}
          </button>
        </div>
      ) : !cameras.length ? (
        <div className="rounded-2xl border border-white/[0.08] bg-[#10211e] px-6 py-12 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-200/15 bg-emerald-300/[0.07]" aria-hidden="true">
            <span className="h-5 w-7 rounded border border-emerald-200/50 after:mx-auto after:mt-1.5 after:block after:h-2 after:w-2 after:rounded-full after:bg-emerald-200/60" />
          </div>
          <h2 className="text-base font-semibold text-[#eff7f2]">No reviewed beach cameras nearby yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-emerald-50/50">
            We haven’t verified a suitable beach or surf view within 25 km of {location.name} yet. Unreviewed, unclear, and unrelated cameras stay hidden.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-emerald-50/45">
              {cameras.length} nearby {cameras.length === 1 ? "camera" : "cameras"}
            </p>
            <div className="flex items-center gap-3">
              {webcamsQuery.isFetching && <span className="text-[11px] text-emerald-50/40">Checking camera list…</span>}
              {webcamsQuery.data?.fetchedAt && (
                <p className="text-[11px] text-emerald-50/35">
                  List checked {relativeAge(webcamsQuery.data.fetchedAt).label.replace(/^Updated /, "")}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {sortedCameras.map((camera) => {
              const key = imageKey(camera);
              const age = relativeAge(camera.lastUpdatedOn);
              const isOld = age.ageMs !== null && age.ageMs > 60 * 60_000;
              const imageFailed = Boolean(failedImages[key]);

              return (
                <article key={camera.id} className="border-b border-white/[0.08] pb-6">
                  <div className="flex min-h-[150px] items-center justify-center sm:min-h-[220px]">
                    {imageFailed ? (
                      <div className="flex min-h-[120px] flex-col items-center justify-center gap-3 px-5 text-center sm:min-h-[180px]">
                        <span className="text-sm text-emerald-50/70">This camera image could not be loaded.</span>
                        {retryButton(camera)}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(event) => {
                          viewerTrigger.current = event.currentTarget;
                          setSelectedCameraId(camera.id);
                        }}
                        aria-label={`Expand image: ${camera.title}`}
                        className="block max-w-full cursor-zoom-in rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-emerald-300"
                      >
                        <img
                          key={`${key}:${imageRevisions[key] ?? 0}`}
                          src={camera.previewUrl}
                          alt={`Latest snapshot from ${camera.title}`}
                          loading="lazy"
                          onError={() => setImageFailed(key)}
                          className="block h-auto w-auto max-h-[560px] max-w-full object-contain"
                        />
                      </button>
                    )}
                  </div>
                  {dayHistoryUrl(camera) && (
                    <section className="mt-4" aria-label={`Past 24 hours at ${camera.title}`}>
                      <h3 className="mb-2 text-xs font-semibold tracking-wide text-emerald-100/70">Past 24 hours</h3>
                      <div className="w-full overflow-hidden rounded-lg border border-white/[0.08] bg-[#10211e]">
                        <iframe
                          src={dayHistoryUrl(camera)!}
                          title={`Windy day history for ${camera.title}`}
                          loading="lazy"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                          className="block aspect-video w-full border-0"
                        />
                      </div>
                    </section>
                  )}
                  <div className="pt-3">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-white/15 bg-[#091512]/75 px-2.5 py-1 text-[11px] font-medium text-white/90">
                          {camera.viewStatus === "approved" ? "Approved spot view" : `Nearby · ${camera.distanceKm.toFixed(1)} km away`}
                        </span>
                        {isOld && (
                          <span className="rounded-full border border-amber-200/30 bg-[#392d17]/90 px-2.5 py-1 text-[11px] font-semibold text-amber-100">
                            Older image
                          </span>
                        )}
                      </div>
                      <h2 className="truncate text-[17px] font-semibold tracking-[-0.02em] text-[#e7f1ec]">{camera.title}</h2>
                      <p className={`mt-1 text-xs ${isOld ? "text-amber-200/75" : "text-emerald-100/50"}`}>
                        {age.label}{isOld ? " · may not reflect current conditions" : ""}
                      </p>
                      {camera.viewStatus === "approved" && (
                        <p className="mt-1 text-xs text-emerald-100/50">{camera.distanceKm.toFixed(1)} km away</p>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}

      {selectedCamera && (
        <SnapshotViewer
          open
          src={selectedCamera.previewUrl}
          title={selectedCamera.title}
          imageFailed={Boolean(failedImages[imageKey(selectedCamera)])}
          retrying={refreshingCameraIds.has(selectedCamera.id)}
          onRetry={() => void refreshCameraImage(selectedCamera.id)}
          onImageError={() => setImageFailed(imageKey(selectedCamera))}
          onCloseFocus={() => viewerTrigger.current?.focus()}
          onOpenChange={(open) => {
            if (!open) setSelectedCameraId(null);
          }}
        />
      )}

      {webcamsQuery.isError && webcamsQuery.data && (
        <div role="status" className="mt-4 rounded-xl border border-amber-200/20 p-4 text-xs text-amber-100">
          <p>The camera list could not be refreshed. These snapshots may be out of date.</p>
          <button type="button" onClick={retryMetadata} disabled={webcamsQuery.isFetching}
            className="mt-2 rounded-lg border border-amber-200/30 px-3 py-2 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-300 disabled:opacity-55">
            {webcamsQuery.isFetching ? "Retrying…" : "Retry"}
          </button>
        </div>
      )}
      <footer className="mt-8 border-t border-white/[0.08] pt-4 text-xs text-emerald-50/45">
        Webcams provided by{" "}
        <a href="https://www.windy.com/" target="_blank" rel="noopener noreferrer" className="font-semibold text-emerald-200/80 underline decoration-emerald-200/30 underline-offset-4 hover:text-emerald-100">
          windy.com
        </a>
        {" · "}
        <a href="https://www.windy.com/webcams/add" target="_blank" rel="noopener noreferrer" className="text-emerald-200/65 underline decoration-emerald-200/20 underline-offset-4 hover:text-emerald-100">
          Add a webcam
        </a>
      </footer>
    </section>
  );
}

export default function LiveImages({ location }: LiveImagesProps) {
  return <LiveImagesForLocation key={location.id} location={location} />;
}
